import {
  AnalyticsReference,
  Code,
  Compass,
  Copy,
  Download,
  Information,
  Reset,
  TableShortcut,
  ThumbsDown,
  ThumbsUp,
} from '@carbon/icons-react';
import * as React from 'react';

import {
  VoiceboxChatActionItem,
  VoiceboxChatActions,
} from 'bones/components/molecules/Voicebox/chat/VoiceboxChatActions';
import { VoiceboxChatFeedbackDialog } from 'bones/components/molecules/Voicebox/chat/VoiceboxChatFeedbackDialog';
import { useVoiceboxConversation } from 'bones/components/molecules/Voicebox/context/VoiceboxConversation';
import { useVoiceboxSchema } from 'bones/components/molecules/Voicebox/context/VoiceboxSchema';
import {
  VoiceboxApp,
  VoiceboxRequestMessage,
  VoiceboxResponseActionType,
  VoiceboxResponseMessage,
} from 'bones/constants/voicebox/message';
import { VoiceboxSettings } from 'bones/constants/voicebox/settings';
import { LoadingIcon } from 'bones/design-system/components/icons/LoadingIcon';
import { useIsMounted } from 'bones/utils/hooks/useIsMounted';
import { addDirective } from 'bones/utils/voicebox/directive';

export interface VoiceboxChatMessageResponseActionsProps {
  hasChart: boolean;
  hasTable: boolean;
  isChartShown: boolean;
  isQueryBuilderShown: boolean;
  isTableShown: boolean;
  message: VoiceboxResponseMessage;
  messageIdx: number;
  messagesRef: React.RefObject<HTMLDivElement>;
  sendMessage: (message: string) => void;
  sendMessageDisabled: boolean;
  setIsChartShown: (isChartShown: boolean) => void;
  setIsQueryBuilderShown: (isQueryBuilderShown: boolean) => void;
  setIsTableShown: (isTableShown: boolean) => void;
  settings: VoiceboxSettings;
}

export const VoiceboxChatMessageResponseActions: React.VFC<
  VoiceboxChatMessageResponseActionsProps
> = ({
  hasChart,
  hasTable,
  isChartShown,
  isQueryBuilderShown,
  isTableShown,
  message,
  messageIdx,
  messagesRef,
  sendMessage,
  sendMessageDisabled,
  setIsChartShown,
  setIsQueryBuilderShown,
  setIsTableShown,
  settings,
}) => {
  const isMounted = useIsMounted();
  const { config, copy, testIds, clipboard, links, queryBuilder } =
    useVoiceboxSchema();
  const {
    downloadCsv,
    isLoadingFeedback,
    loadingDownloadMessageId,
    messages,
    sendFeedback,
  } = useVoiceboxConversation();

  const prevRequestMessage = messages
    .slice(0, messageIdx)
    .reverse()
    .find((message): message is VoiceboxRequestMessage =>
      Boolean(message.isRequest)
    );
  const sparqlQueryActions = message.actions?.filter(
    (action) => action.type === VoiceboxResponseActionType.SPARQL
  );
  const hasSparqlAction = Boolean(sparqlQueryActions?.length);

  const rewrittenQuestion = message.actions?.find(
    (action) => action.type === VoiceboxResponseActionType.REWRITTEN_QUERY
  )?.value;

  const showRegenerateResponse = Boolean(
    config.app !== VoiceboxApp.DESIGNER && prevRequestMessage
  );
  const showTable = Boolean(!links.openLinksInPanel && hasTable);
  const showCopyResponse = clipboard.isClipboardEnabled;
  const showCopyQuery = clipboard.isClipboardEnabled && hasSparqlAction;
  const showExplainResponse = Boolean(
    !links.openLinksInPanel && queryBuilder && hasSparqlAction
  );

  const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = React.useState(false);

  const handleRegenerateResponse = () => {
    // istanbul ignore if
    if (sendMessageDisabled) {
      return;
    }

    //  istanbul ignore else
    if (prevRequestMessage) {
      sendMessage(addDirective(prevRequestMessage.query, 'nohistory'));
    }
  };

  const handleSendFeedback = async (score: number) => {
    const { succeeded } = await sendFeedback(message, score);
    if (isMounted.current && succeeded && !score) {
      setIsFeedbackDialogOpen(true);
    }
  };

  const handleCopyResponse = () => {
    clipboard.copyToClipboard(
      prevRequestMessage
        ? `${prevRequestMessage.query}\n\n${message.result}`
        : message.result
    );
  };

  const handleCopyQuery = () => {
    clipboard.copyToClipboard(
      sparqlQueryActions?.[0].value || /* istanbul ignore next */ ''
    );
  };

  const handleDownloadTable = () => {
    downloadCsv(message, settings);
  };

  const leftActions: VoiceboxChatActionItem[] = [];
  if (showRegenerateResponse) {
    leftActions.push({
      disabled: sendMessageDisabled,
      icon: Reset,
      label: copy.message.menu.regenerateResponse,
      onClick: sendMessageDisabled ? undefined : handleRegenerateResponse,
      testId: testIds.messages.menu.regenerateResponse,
    });
  }
  if (showTable) {
    leftActions.push({
      icon: TableShortcut,
      label: isTableShown
        ? copy.message.menu.csvTable.hide
        : copy.message.menu.csvTable.show,
      onClick: () => setIsTableShown(!isTableShown),
      testId: testIds.messages.menu.showCsvTable,
    });
  }
  if (hasChart) {
    leftActions.push({
      icon: AnalyticsReference,
      label: isChartShown
        ? copy.message.menu.chart.hide
        : copy.message.menu.chart.show,
      onClick: () => setIsChartShown(!isChartShown),
      testId: testIds.messages.menu.showChart,
    });
  }

  const rightActions: VoiceboxChatActionItem[] = [];
  if (rewrittenQuestion) {
    rightActions.push({
      icon: Information,
      tooltip: copy.message.menu.questionRewrittenAs(rewrittenQuestion),
      testId: testIds.messages.menu.rewrittenQuestion,
    });
  }

  if (!message.success) {
    // do not add feedback options for failure message
  } else if (isLoadingFeedback) {
    rightActions.push({
      disabled: true,
      icon: LoadingIcon,
      testId: testIds.messages.menu.feedback.loading,
    });
  } else if (message.feedbackScore === undefined) {
    rightActions.push(
      {
        disabled: isLoadingFeedback,
        icon: ThumbsUp,
        testId: testIds.messages.menu.feedback.positive,
        onClick: () => handleSendFeedback(1),
      },
      {
        disabled: isLoadingFeedback,
        icon: ThumbsDown,
        testId: testIds.messages.menu.feedback.negative,
        onClick: () => handleSendFeedback(0),
      }
    );
  } else if (message.feedbackScore) {
    rightActions.push({
      icon: ThumbsUp,
      testId: testIds.messages.menu.feedback.positive,
    });
  } else {
    rightActions.push({
      icon: ThumbsDown,
      testId: testIds.messages.menu.feedback.negative,
    });
  }

  const menuActions: VoiceboxChatActionItem[] = [];
  if (showCopyResponse) {
    menuActions.push({
      icon: Copy,
      label: copy.message.menu.copyResponse,
      onClick: handleCopyResponse,
      testId: testIds.messages.menu.copyResponse,
    });
  }
  if (showCopyQuery) {
    menuActions.push({
      icon: Code,
      label: copy.message.menu.copyQuery,
      onClick: handleCopyQuery,
      testId: testIds.messages.menu.copyQuery,
    });
  }
  if (showExplainResponse) {
    menuActions.push({
      badgeProps: isQueryBuilderShown ? undefined : { children: 'BETA' },
      icon: Compass,
      label: isQueryBuilderShown
        ? copy.message.menu.queryBuilder.hide
        : copy.message.menu.queryBuilder.show,
      onClick: () => setIsQueryBuilderShown(!isQueryBuilderShown),
      testId: testIds.messages.menu.queryBuilder,
    });
  }
  if (hasSparqlAction) {
    menuActions.push({
      disabled: Boolean(loadingDownloadMessageId),
      icon: Download,
      label: copy.message.menu.downloadResults,
      onClick: handleDownloadTable,
      testId: testIds.messages.menu.downloadCsv,
    });
  }

  return (
    <>
      <VoiceboxChatActions
        leftActions={leftActions}
        menuActions={menuActions}
        messagesRef={messagesRef}
        rightActions={rightActions}
      />
      <VoiceboxChatFeedbackDialog
        isOpen={isFeedbackDialogOpen}
        message={message}
        onClose={() => setIsFeedbackDialogOpen(false)}
      />
    </>
  );
};
